/**
 * org-referral-card.tsx — "Mã giới thiệu" của tổ chức (Cài đặt → Thông tin công ty).
 * Chủ tổ chức nhập mã của người giới thiệu trong N ngày đầu sau khi tạo tổ chức (khi chưa thanh toán gói).
 * Tự điền mã nếu trình duyệt còn vé giới thiệu từ link /r/:mã.
 */
import { useState } from 'react'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Handshake, Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { apiError } from '@/lib/api-client'
import { codeError, normalizeCode } from '@/lib/affiliate'
import { readStoredRef } from '@/lib/affiliate-ref-storage'
import { useBindOrgReferral, useOrgReferral } from '@/hooks/use-affiliate'
import { useAuthStore } from '@/stores/auth-store'

export function OrgReferralCard() {
  const { t } = useTranslation('growth')
  const role = useAuthStore((s) => s.user?.role)
  const { data } = useOrgReferral()
  const bind = useBindOrgReferral()
  const [code, setCode] = useState(() => readStoredRef()?.code ?? '')
  if (!data) return null
  if (!data.referred && !data.canEnter) return null
  const err = code ? codeError(code) : null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Handshake className="h-4 w-4 text-primary" /> {t('orgReferralCard.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {data.referrer && (
          <p>
            {t('orgReferralCard.referredByPre')} <b>{data.referrer.partnerName}</b> {t('orgReferralCard.referredByPost', { code: data.referrer.code })}
            {data.locked ? ` ${t('orgReferralCard.locked')}` : '.'}
          </p>
        )}
        {data.canEnter && (role === 'owner' ? (
          <>
            <p className="text-muted-foreground">
              {data.referrer ? t('orgReferralCard.changeReferrer') : t('orgReferralCard.enterReferrer')} {t('orgReferralCard.before')}
              {' '}{data.deadline ? dayjs(data.deadline).format('DD/MM/YYYY') : ''} {t('orgReferralCard.beforeFirstPayment')}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Input className="max-w-[220px]" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={t('orgReferralCard.codePlaceholder')} maxLength={20} />
              <Button
                disabled={!code || !!err || bind.isPending}
                onClick={() => bind.mutate(normalizeCode(code), {
                  onSuccess: () => toast.success(t('orgReferralCard.applied')),
                  onError: (e) => toast.error(apiError(e)),
                })}
              >
                {bind.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {t('orgReferralCard.apply')}
              </Button>
            </div>
            {err && <p className="text-xs text-destructive">{err}</p>}
          </>
        ) : (
          <p className="text-muted-foreground">{t('orgReferralCard.ownerOnly')}</p>
        ))}
      </CardContent>
    </Card>
  )
}
